/**
 * Built-in custom master data types — always provisioned per org (keep app + api in lockstep).
 */
import type { CustomMasterDataFieldDef, CustomMasterDataLinkRules } from './customMasterData';

export const STATIC_ASSET_GROUP_GROUP_ID = 'static_asset_group';

export const ROOM_ASSET_TEMPLATE_CODE = 'ROOM_ASSET';
export const ASSET_GROUP_TEMPLATE_CODE = 'ASSET_GROUP';

export const BUILTIN_ROOM_ASSET_TYPE_ID = 'cmdt_builtin_room_asset';
export const BUILTIN_ASSET_GROUP_TYPE_ID = 'cmdt_builtin_asset_group';

/** All static sidebar group ids — never deleted on custom-data migration. */
export const RESERVED_STATIC_MASTER_DATA_GROUP_IDS = [
  'static_custom_cfg',
  'static_custom_op',
  'static_custom_struct',
  STATIC_ASSET_GROUP_GROUP_ID,
] as const;

/** Dynamic connect-master-data field on any host record (comma-separated ids). */
export const connectedMasterDataFieldKey = (typeCode: string): string => (
  `connected_${String(typeCode || '').trim().toLowerCase()}_record_id`
);

export const isConnectedMasterDataFieldKey = (key: string): boolean => (
  /^connected_.+_record_id$/i.test(String(key || '').trim())
);

export const parseConnectedMasterDataIdList = (raw: unknown): string[] => (
  String(raw || '')
    .split(/[,;|]/)
    .map((part) => part.trim())
    .filter(Boolean)
);

/** Asset template has no industry-specific fields — connect master data on the record. */
export const ROOM_ASSET_FIELD_SCHEMA: CustomMasterDataFieldDef[] = [];

export const ROOM_ASSET_LINK_RULES: CustomMasterDataLinkRules = {};

export const ASSET_GROUP_FIELD_SCHEMA: CustomMasterDataFieldDef[] = [];

export const ASSET_GROUP_LINK_RULES: CustomMasterDataLinkRules = {};

export type BuiltinCustomMasterDataTypeDef = {
  id: string
  code: string
  name: string
  tier: 'configuration' | 'operation'
  groupId: string
  groupCode: string
  groupName: string
  groupSortOrder: number
  fields: CustomMasterDataFieldDef[]
  templateSourceKind?: string
  linkRules?: CustomMasterDataLinkRules
  sortOrder: number
};

export const BUILTIN_CUSTOM_MASTER_DATA_TYPES: BuiltinCustomMasterDataTypeDef[] = [
  {
    id: BUILTIN_ROOM_ASSET_TYPE_ID,
    code: ROOM_ASSET_TEMPLATE_CODE,
    name: 'Asset fields',
    tier: 'operation',
    groupId: 'static_custom_op',
    groupCode: 'CUSTOM_OP',
    groupName: 'Custom',
    groupSortOrder: 50,
    fields: ROOM_ASSET_FIELD_SCHEMA,
    templateSourceKind: 'asset',
    linkRules: ROOM_ASSET_LINK_RULES,
    sortOrder: 0,
  },
  {
    id: BUILTIN_ASSET_GROUP_TYPE_ID,
    code: ASSET_GROUP_TEMPLATE_CODE,
    name: 'Asset group',
    tier: 'configuration',
    groupId: STATIC_ASSET_GROUP_GROUP_ID,
    groupCode: 'ASSET_GROUP',
    groupName: 'Asset group',
    groupSortOrder: 3,
    fields: ASSET_GROUP_FIELD_SCHEMA,
    templateSourceKind: 'asset_group',
    linkRules: ASSET_GROUP_LINK_RULES,
    sortOrder: 1,
  },
];

export const builtinCustomMasterDataTypeByCode = (
  code: string,
): BuiltinCustomMasterDataTypeDef | null => (
  BUILTIN_CUSTOM_MASTER_DATA_TYPES.find(
    (row) => row.code.toUpperCase() === String(code || '').trim().toUpperCase(),
  ) || null
);
